import { request } from './http'

export type IngestReviewStatus = 'pending' | 'edited' | 'accepted' | 'rejected'
export type IngestReviewAction = 'accept' | 'reject'

export interface IngestEvidence {
  chapter_id: string
  chapter_no: number
  char_start: number
  char_end: number
  snippet: string
  match_type?: 'exact' | 'approximate' | 'provided' | 'unverified'
  field: string
  confidence?: number | null
}

export interface IngestReview {
  id: string
  chapter_no: number
  canonical_name: string
  surface_forms: string[]
  proposed_state: Record<string, unknown>
  edited_payload?: Record<string, unknown>
  evidence: IngestEvidence[]
  confidence: number | null
  status: IngestReviewStatus
}

export interface IngestReviewListResponse {
  items: IngestReview[]
  page: number
  limit: number
}

export interface ListIngestReviewsParams {
  branchId?: string
  status?: IngestReviewStatus | 'all'
  chapterNo?: number | null
  page?: number
  limit?: number
}

export interface IngestExtractRequest {
  chapter_ids?: string[]
  all?: boolean
}

export interface IngestBatchRequest {
  ids: string[]
  action: IngestReviewAction
}

const basePath = (projectId: string) => `/v1/projects/${encodeURIComponent(projectId)}/ingest`
const reviewPath = (projectId: string, reviewId: string) =>
  `${basePath(projectId)}/reviews/${encodeURIComponent(reviewId)}`

function listPath(projectId: string, params: ListIngestReviewsParams = {}) {
  const qs = new URLSearchParams()
  if (params.branchId) qs.set('branch_id', params.branchId)
  if (params.status && params.status !== 'all') qs.set('status', params.status)
  if (params.chapterNo) qs.set('chapter_no', String(params.chapterNo))
  if (params.page) qs.set('page', String(params.page))
  if (params.limit) qs.set('limit', String(params.limit))
  const query = qs.toString()
  return `${basePath(projectId)}/reviews${query ? `?${query}` : ''}`
}

export const ingestReviewApi = {
  settings: (projectId: string, branchId = 'main'): Promise<{ entity_review_mode: string; pending_count: number }> =>
    request(`${basePath(projectId)}/settings?branch_id=${encodeURIComponent(branchId)}`),
  setMode: (projectId: string, entity_review_mode: string) =>
    request(`${basePath(projectId)}/settings`, { method: 'PUT', body: { entity_review_mode } }),
  list: (projectId: string, params?: ListIngestReviewsParams): Promise<IngestReviewListResponse> =>
    request<IngestReviewListResponse>(listPath(projectId, params)),
  extract: (projectId: string, body: IngestExtractRequest) =>
    request(`${basePath(projectId)}/extract`, { method: 'POST', body }),
  update: (projectId: string, reviewId: string, editedPayload: Record<string, unknown>) =>
    request(`${reviewPath(projectId, reviewId)}`, { method: 'PUT', body: { edited_payload: editedPayload } }),
  accept: (projectId: string, reviewId: string) =>
    request(`${reviewPath(projectId, reviewId)}/accept`, { method: 'POST' }),
  reject: (projectId: string, reviewId: string) =>
    request(`${reviewPath(projectId, reviewId)}/reject`, { method: 'POST' }),
  batch: (projectId: string, body: IngestBatchRequest) =>
    request(`${basePath(projectId)}/reviews/batch`, { method: 'POST', body }),
}
